import React from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { Image, ImageAndQueries, CaseWithImages, QueryStatus } from "../../models";
import { toggleImageSelectorExpanded } from "../../slices/imageSelector";
import Expandable from "../Expandable";
import { Text, Radio } from "@blasterjs/core";
import { faFileArrowDown } from "@fortawesome/free-solid-svg-icons";
import { AppDispatch } from "../../store";
import { openDownloadImageDialog } from "../../slices/downloadImageDialog";
import { cancelImageAnnotation, selectImage } from "../../slices/caseImageViewer";
import { RolePermissions } from "../../permissions";
import DownloadImageDialog from "../DownloadImageDialog";

interface ImageListItemProps {
  readonly image: Image;
  readonly index: number;
  readonly isShownToReader?: boolean;
  readonly imageQueries?: ImageAndQueries;
}

const ImageListItem = ({
  image,
  index,
  isShownToReader = true,
  imageQueries
}: ImageListItemProps) => {
  const dispatch = useAppDispatch();

  const loggedInUser = useAppSelector(state => state.auth.loggedInUser);

  const userCanDownloadImages =
    "resource" in loggedInUser &&
    loggedInUser.resource.can([RolePermissions.IM_ImagesListTab_DownloadImages]);

  const selectedImage = useAppSelector(state => state.caseImageViewer.imageWithAnnotations);

  const downloadImage = (image: Image, dispatch: AppDispatch) => {
    dispatch(openDownloadImageDialog(image));
  };

  const onSelectImage = (image: Image, dispatch: AppDispatch) => async () => {
    // NOTE: It's important to dispatch this action to cancel any in-progress annotation to ensure the
    // annotation code cleans up after itself properly.
    dispatch(cancelImageAnnotation());
    await dispatch(selectImage(image.id));
  };

  const BiopsyLocationText = styled.label`
    color: #ccc;
  `;

  const ImageHiddenFromReaderLabel = styled.span`
    color: gray;
  `;

  const ImageContainer = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    width: 100%;
  `;

  const RadioContainer = styled.div`
    width: 2.5rem;
    flex-shrink: 0;
    border-left: 2px solid transparent;
  `;

  const ResolvedRadioContainer = styled.div`
    width: 2.5rem;
    flex-shrink: 0;
    border-left: 2px solid gray;
  `;

  const UnresolvedRadioContainer = styled.div`
    width: 2.5rem;
    flex-shrink: 0;
    border-left: 2px solid #dc3831;
  `;

  const ContentContainer = styled.div`
    flex: 1;
  `;

  const DownloadContainer = styled.div`
    width: 2rem;
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    padding-top: 0.2rem;
  `;

  const RadioLabel = styled.label`
    cursor: pointer;
  `;

  const radioId = `ImageSelector-${index}`;

  function imageQueriesWereClosed(imageQueries: ImageAndQueries): boolean {
    return imageQueries
      .map(imageAndQuery => imageAndQuery.lastLabQuery)
      .some(query => query && query.queryStatus == QueryStatus.Closed);
  }

  function imageQueriesNeedResolution(imageQueries: ImageAndQueries): boolean {
    return imageQueries
      .map(imageAndQuery => imageAndQuery.lastLabQuery)
      .some(query => query && query.queryStatus == QueryStatus.Open);
  }

  const RadioContainerComponent = imageQueries
    ? imageQueriesNeedResolution(imageQueries)
      ? UnresolvedRadioContainer
      : imageQueriesWereClosed(imageQueries)
      ? ResolvedRadioContainer
      : RadioContainer
    : RadioContainer;

  return (
    <li key={image.id}>
      <ImageContainer>
        <RadioContainerComponent>
          <Radio
            id={radioId}
            checked={
              selectedImage !== null &&
              "resource" in selectedImage &&
              selectedImage.resource.imageAndQuery.image.id === image.id
            }
            onChange={onSelectImage(image, dispatch)}
          />
        </RadioContainerComponent>
        <ContentContainer>
          <RadioLabel htmlFor={radioId}>
            {isShownToReader ? (
              <Text>{image.name}</Text>
            ) : (
              <ImageHiddenFromReaderLabel>{image.name}</ImageHiddenFromReaderLabel>
            )}
            <br />
            <BiopsyLocationText>{image.biopsyLocation}</BiopsyLocationText>
          </RadioLabel>
        </ContentContainer>
        {userCanDownloadImages && (
          <DownloadContainer>
            <FontAwesomeIcon
              icon={faFileArrowDown}
              style={{
                width: "1.5rem",
                height: "1.5rem",
                color: "#9A9DA0",
                cursor: "pointer",
                transform: "scale(1.2)"
              }}
              onClick={() => downloadImage(image, dispatch)}
              title="Download Image"
            />
          </DownloadContainer>
        )}
      </ImageContainer>
    </li>
  );
};

interface Props {
  readonly histoCase: CaseWithImages;
}

const ImageSelector = ({ histoCase }: Props) => {
  const dispatch = useAppDispatch();
  const isImageSelectorPanelExpanded = useAppSelector(
    state => state.imageSelector.isImageSelectorPanelExpanded
  );
  const selectedImage = useAppSelector(state => state.caseImageViewer.imageWithAnnotations);

  const selectedImageIndex =
    selectedImage && "resource" in selectedImage
      ? "imageQueries" in histoCase
        ? histoCase.imageQueries.findIndex(
            imageQuery => imageQuery[0]?.image.id === selectedImage.resource.imageAndQuery.image.id
          ) + 1
        : histoCase.images.findIndex(
            image => image.id === selectedImage.resource.imageAndQuery.image.id
          ) + 1
      : 0;

  const ImageSelectorPanel = styled.div`
    display: flex;
    margin: 15px;
    padding: 0;
    z-index: 30000;
    left: 15px;
    top: 15px;
    box-shadow: 0 0 0 1px rgba(16, 22, 26, 0.1), 0 0 0 rgba(16, 22, 26, 0),
      0 1px 1px rgba(16, 22, 26, 0.2);
    width: fit-content;
  `;

  const ImageList = styled.ul`
    padding: 10px;
    max-height: 50vh;
    overflow-y: auto;
  `;

  const imageCount =
    "imageQueries" in histoCase ? histoCase.imageQueries.length : histoCase.images.length;
  const title = `Images (${selectedImageIndex}/${imageCount})`;

  return (
    <ImageSelectorPanel>
      <Expandable
        label={title}
        isExpanded={isImageSelectorPanelExpanded}
        onToggle={() => dispatch(toggleImageSelectorExpanded())}
        alwaysShowLabel={true}
      >
        <ImageList>
          {"imageQueries" in histoCase
            ? histoCase.imageQueries.flatMap((imageQuery, queryIndex) =>
                imageQuery.map((imageAndQuery, imageIndex) =>
                  ImageListItem({
                    image: imageAndQuery.image,
                    index: queryIndex * 1000 + imageIndex,
                    isShownToReader: imageAndQuery.isShownToReader,
                    imageQueries: imageQuery.filter(
                      q => q.lastLabQuery?.objectId === imageAndQuery.image.id
                    )
                  })
                )
              )
            : histoCase.images.map((image, index) => {
                return ImageListItem({ image, index });
              })}
        </ImageList>
      </Expandable>
      <DownloadImageDialog />
    </ImageSelectorPanel>
  );
};

export default ImageSelector;
